import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Eye, EyeOff, Scale } from 'lucide-react'
import { useAuth } from '../lib/AuthContext'
import { useI18n } from '../lib/i18n'

const LABELS = {
  zh: {
    title: '创建账号',
    subtitle: '注册您的 AI 律师助手账号',
    displayName: '姓名',
    email: '邮箱地址',
    password: '密码',
    confirmPassword: '确认密码',
    firmName: '律所名称（选填）',
    register: '注册',
    registering: '注册中...',
    hasAccount: '已有账号？',
    login: '立即登录',
    brandName: 'AI Lawyer',
    brandDesc: '智能法律助手',
    errorGeneric: '注册失败，请稍后重试',
    errorMismatch: '两次输入的密码不一致',
    errorShort: '密码至少需要6位',
  },
  en: {
    title: 'Create Account',
    subtitle: 'Register your AI Lawyer account',
    displayName: 'Full Name',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    firmName: 'Firm Name (optional)',
    register: 'Register',
    registering: 'Registering...',
    hasAccount: 'Already have an account?',
    login: 'Sign in',
    brandName: 'AI Lawyer',
    brandDesc: 'Intelligent Legal Assistant',
    errorGeneric: 'Registration failed. Please try again.',
    errorMismatch: 'Passwords do not match',
    errorShort: 'Password must be at least 6 characters',
  },
}

export function Register() {
  const { lang } = useI18n()
  const t = LABELS[lang] ?? LABELS.zh
  const { login } = useAuth()
  const navigate = useNavigate()

  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [firmName, setFirmName] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 6) {
      setError(t.errorShort)
      return
    }
    if (password !== confirmPw) {
      setError(t.errorMismatch)
      return
    }

    setLoading(true)
    try {
      const res = await fetch('/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          display_name: displayName,
          firm_name: firmName || null,
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => null)
        setError(data?.detail || t.errorGeneric)
        return
      }
      const data = await res.json()
      login(data)
      navigate('/', { replace: true })
    } catch {
      setError(t.errorGeneric)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side — branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 items-center justify-center p-12">
        <div className="text-center">
          <div className="w-20 h-20 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Scale className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{t.brandName}</h1>
          <p className="text-blue-200 text-lg">{t.brandDesc}</p>
        </div>
      </div>

      {/* Right side — form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <Scale className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-800">{t.brandName}</span>
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-1">{t.title}</h2>
          <p className="text-gray-500 mb-8">{t.subtitle}</p>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t.displayName}
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t.email}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t.firmName}
              </label>
              <input
                type="text"
                value={firmName}
                onChange={(e) => setFirmName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t.password}
              </label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t.confirmPassword}
              </label>
              <input
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-blue-600 text-white font-medium text-sm hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? t.registering : t.register}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            {t.hasAccount}{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
              {t.login}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
